import { FolderX } from 'lucide-react';
import { Link, useParams } from 'react-router-dom';
import { useMinLoading } from '@/hooks/use-min-loading';
import {
	Breadcrumb,
	BreadcrumbItem,
	BreadcrumbLink,
	BreadcrumbList,
	BreadcrumbPage,
	BreadcrumbSeparator,
} from '@/ui/components/ui/breadcrumb';
import { Card, CardContent } from '@/ui/components/ui/card';
import { Spinner } from '@/ui/components/ui/spinner';
import { StatusPlaceholder } from '@/ui/components/ui/status-placeholder';
import {
	Tabs,
	TabsContent,
	TabsList,
	TabsTrigger,
} from '@/ui/components/ui/tabs';
import { AdminLayout } from '../components/AdminLayout';
import { ConfigurationTab } from '../components/project/ConfigurationTab';
import { FeatureRequestsTab } from '../components/project/FeatureRequestsTab';
import { ProjectHeader } from '../components/project/ProjectHeader';
import { UserAccessTab } from '../components/project/UserAccessTab';
import {
	useAdminFeatureRequests,
	useAdminProject,
	useAdminRegenerateHmacSecret,
	useAdminUpdateProject,
} from '../hooks/use-admin';

export function AdminProjectPage() {
	const { id } = useParams<{ id: string }>();

	const { data: projectData, loading: projectLoading } = useAdminProject(
		id ?? '',
	);
	const {
		data: frData,
		loading: frLoading,
		error: frError,
		refetch: refetchFr,
	} = useAdminFeatureRequests(id ?? '');
	const [updateProject] = useAdminUpdateProject();
	const [regenerateHmacSecret] = useAdminRegenerateHmacSecret();

	const showLoader = useMinLoading(projectLoading);

	const project = projectData?.adminProject ?? null;
	const featureRequests = frData?.adminFeatureRequests ?? [];

	const canEdit = project?.canEdit ?? false;

	if (showLoader) {
		return (
			<AdminLayout>
				<div className='flex items-center justify-center py-32'>
					<Spinner size='lg' />
				</div>
			</AdminLayout>
		);
	}

	if (!project) {
		return (
			<AdminLayout>
				<Card>
					<CardContent>
						<StatusPlaceholder
							icon={FolderX}
							title='Project not found'
							description='This project may have been deleted or does not exist.'
							variant='error'
						/>
					</CardContent>
				</Card>
			</AdminLayout>
		);
	}

	return (
		<AdminLayout>
			<Breadcrumb className='mb-6'>
				<BreadcrumbList>
					<BreadcrumbItem>
						<BreadcrumbLink asChild>
							<Link to='/admin'>Projects</Link>
						</BreadcrumbLink>
					</BreadcrumbItem>
					<BreadcrumbSeparator />
					<BreadcrumbItem>
						<BreadcrumbPage>{project.name}</BreadcrumbPage>
					</BreadcrumbItem>
				</BreadcrumbList>
			</Breadcrumb>

			<ProjectHeader
				project={project}
				updateProject={updateProject}
				canEdit={canEdit}
			/>

			<Tabs defaultValue='feature-requests'>
				<TabsList className='mb-6'>
					<TabsTrigger value='feature-requests'>
						Feature Requests
					</TabsTrigger>
					{canEdit && (
						<>
							<TabsTrigger value='user-access'>
								User Access
							</TabsTrigger>
							<TabsTrigger value='configuration'>
								Configuration
							</TabsTrigger>
						</>
					)}
				</TabsList>

				<TabsContent value='feature-requests'>
					<FeatureRequestsTab
						featureRequests={featureRequests}
						loading={frLoading}
						error={!!frError}
						onRefresh={refetchFr}
					/>
				</TabsContent>

				{canEdit && (
					<>
						<TabsContent value='user-access'>
							<UserAccessTab projectId={project.id} />
						</TabsContent>

						<TabsContent value='configuration'>
							<ConfigurationTab
								project={project}
								updateProject={updateProject}
								regenerateHmacSecret={regenerateHmacSecret}
								canEdit={canEdit}
							/>
						</TabsContent>
					</>
				)}
			</Tabs>
		</AdminLayout>
	);
}
